/**
 *
 * Copyright © 2017 Easymeeting AS. All rights reserved.
 *
 * This software is produced by Easymeeting AS.
 * Unauthorized redistribution, reproduction, or usage of
 * this software in whole or in part without the express
 * written consent of Easymeeting AS is strictly prohibited.
 */
import Vue from 'vue';
import { getUsersByHash } from '@/api/profile';

// presence is true or false
function sortPresence (func) {
  return function (a, b) {
    if (a.presence > b.presence) {
      return -1;
    }
    if (a.presence < b.presence) {
      return 1;
    }
    if (func) {
      return func(a, b);
    } else {
      // a.presence === b.presence
      return 0;
    }
  };
}

// user status is 0,1,2
function sortStatus (func) {
  return function (a, b) {
    if (a.userstatus === 1 && b.userstatus === 2) {
      return -1;
    }
    if (a.userstatus === 1 && b.userstatus === 0) {
      return -1;
    }
    if (a.userstatus === 2 && b.userstatus === 1) {
      return 1;
    }
    if (a.userstatus === 2 && b.userstatus === 0) {
      return -1;
    }
    if (a.userstatus === 0 && b.userstatus === 1) {
      return 1;
    }
    if (a.userstatus === 0 && b.userstatus === 2) {
      return 1;
    }

    if (func) {
      return func(a, b);
    } else {
      // a.userstatus === b.userstatus
      return 0;
    }
  };
}

const sortContactFunc = sortPresence(sortStatus());

function formatContacts (contacts) {
  if (contacts && contacts.length > 0) {
    contacts.forEach((element, index) => {
      if (!element.name) {
        element.fname = element.fname || '';
        element.lname = element.lname || '';
        element.name = `${element.fname} ${element.lname}`.trim();
      }
      element.userstatus = 1;
      contacts[index] = {
        presence: true,
        isMyContact: true,
        ...element,
        isfav: parseInt(element.isfav) === 1
      };
    });
  }
  return contacts;
}

function formatUser (user) {
  if (!user.name) {
    user.fname = user.fname || '';
    user.lname = user.lname || '';
    user.name = `${user.fname} ${user.lname}`.trim();
  }
  user.userstatus = parseInt(user.userstatus);
  user.presence = false;
  user.isfav = false;
  user.isMyContact = false;
  return user;
}

function formatIncoming (state, incoming) {
  if (incoming) {
    const found = state.userPool.find(function (user) {
      if (incoming.fromid === user.userhash) {
        return true;
      }
    });
    if (found) {
      incoming = {
        ...found,
        ...incoming
      };
    }
  }
  return incoming;
}

function isMyContact (state, data) {
  return state.contacts.find(function (contact) {
    if (data.registeredid) {
      return contact.userhash === data.registeredid;
    }
    if (data.id) {
      return contact.id === data.id;
    }

    if (data.userhash && contact.userhash) {
      return contact.userhash === data.userhash;
    }

    if (contact.room) {
      if (data.number_short) {
        return Number(contact.room) === Number(data.number_short);
      }
      if (data.room) {
        return Number(contact.room) === Number(data.room);
      }
      return false;
    }

    if (contact.iscustom) {
      return !!contact.devices.find(function (dev) {
        if (data.number_short) {
          return dev.number === data.number_short;
        }
        if (data.room) {
          return dev.number === data.room;
        }
        if (data.user) {
          return dev.number === data.user;
        }
        return false;
      });
    }
    return false;
  });
}

function formatRecentCalls (state) {
  const out = [];
  if (state.recentCalls && state.recentCalls.length > 0) {
    state.recentCalls.forEach(element => {
      if (element.registeredid) {
        const found = state.userPool.find(
          user => user.userhash === element.registeredid
        );
        if (found) {
          out.push({
            contact: { ...found },
            ...element
          });
        }
      } else {
        element.userhash =
          element.registeredid || element.remoteuser || null;
        element.name = element.remoteuser || element.room;
        out.push({
          contact: {
            ...element,
            isMyContact: false,
            iscustom: false,
            isfav: false,
            presence: true,
            profilepic: null,
            userstatus: 1
          },
          ...element
        });
      }
    });
  }
  return out;
}

function formatSearchResults (state, results, currentUser) {
  const out = [];
  if (results && results.length > 0) {
    results
      .filter(item => {
        if (
          item.userhash &&
          currentUser &&
          item.userhash === currentUser.userhash
        ) {
          return false;
        }
        if (!item.customer || item.customer.trim().length <= 0) {
          return false;
        }
        if (!item.number_short || item.number_short.trim().length <= 0) {
          return false;
        }
        return true;
      })
      .forEach(element => {
        if (element.userhash) {
          const found = state.userPool.find(
            user => user.userhash === element.userhash
          );
          if (found) {
            out.push(found);
          }
        } else {
          const contact = isMyContact(state, element);
          if (contact) {
            out.push(contact);
          } else {
            const nameblocks = element.customer.split(' ');
            const fname = nameblocks[0];
            nameblocks.splice(0, 1);
            const lname = nameblocks.join(' ');
            out.push({
              address: element.address_line1,
              country: element.country,
              defaultdevice: element.defaultdevice,
              dirhash: element.dirhash,
              email: element.email,
              fname,
              isMyContact: false,
              iscustom: false,
              isfav: false,
              lname,
              name: element.customer,
              phone: element.phone,
              presence: true,
              profilepic: element.profilepic,
              room: element.number_short,
              userstatus: 1,
              orgname: element.orgname,
              uid: element.uid,
              userhash: element.userhash
            });
          }
        }
      });
  }
  return removeParticipants(state, out);
}

function removeParticipants (state, results) {
  if (state.meetingParticipants && state.meetingParticipants.length > 0) {
    state.meetingParticipants.forEach(element => {
      const index = results.findIndex(res => {
        return (
          Number(element.number_short) === Number(res.number_short) ||
          (element.userhash && element.userhash === res.userhash) ||
          (element.with && element.with === res.userhash)
        );
      });
      if (index > 0) {
        results.splice(index, 0);
      }
    });
  }
  return results;
}

async function fetchUsers (hashs) {
  let users = [];
  try {
    users = (await getUsersByHash(hashs)) || [];
  } catch (error) {
    // console.error('fetchUsers :', error);
  }

  return users;
}

function fetchContacts (state) {
  return [
    ...state.userPool.filter(user => user.isMyContact),
    ...formatContacts(state.contacts.filter(contact => contact.iscustom))
  ];
}

export default {
  namespaced: true,
  state: {
    contacts: [],
    formattedRecentCalls: [],
    incomingContact: null,
    meetingParticipants: [],
    outgoingContact: null,
    presence: [],
    recentCalls: [],
    searchResults: [],
    userPool: []
  },
  getters: {
    contacts (state) {
      return fetchContacts(state).sort(sortContactFunc);
    },
    favorites (state, getters) {
      return getters.contacts.filter(elem => {
        return elem.isfav;
      });
    },
    incomingContact (state) {
      return state.incomingContact;
    },
    meetingParticipants (state) {
      return state.meetingParticipants;
    },
    outgoingContact (state) {
      return state.outgoingContact;
    },
    recentCalls (state) {
      return formatRecentCalls(state);
    },
    searchResults (state, getters, rootState, rootGetters) {
      return formatSearchResults(
        state,
        state.searchResults,
        rootGetters['AuthStore/profile']
      );
    },
    userPool (state) {
      return state.userPool;
    }
  },
  mutations: {
    addContacts (state, contacts) {
      (contacts || []).forEach(elem => {
        state.contacts.push(elem);
      });
    },
    addUserToPool (state, users) {
      users.forEach(elem => {
        if (elem.userhash) {
          if (!state.userPool.find(user => user.userhash === elem.userhash)) {
            state.userPool.push(formatUser(elem));
          }
        }
      });
    },
    markContacts (state) {
      for (let i = 0; i < state.userPool.length; i++) {
        const user = state.userPool[i];
        const contact = state.contacts.find(
          cnt => cnt.userhash === user.userhash
        );
        if (contact) {
          user.id = contact.id;
          user.isMyContact = true;
          user.isfav = parseInt(contact.isfav) === 1;
        } else {
          delete user.id;
          user.isMyContact = false;
          user.isfav = false;
        }
        user.iscustom = false;
        Vue.set(state.userPool, i, user);
      }
    },
    markPresence (state) {
      for (let i = 0; i < state.userPool.length; i++) {
        const user = state.userPool[i];
        user.presence = !!state.presence.find(
          elem => elem === state.userPool[i].userhash
        );
        Vue.set(state.userPool, i, user);
      }
    },
    removeContact (state, contact) {
      if (contact && contact.id) {
        const index = state.contacts.findIndex(elem => {
          return elem.id === contact.id;
        });
        if (index > -1) {
          state.contacts.splice(index, 1);
        }
      }
    },
    setContacts (state, contacts) {
      state.contacts = contacts;
    },
    setIncomingContact (state, contact) {
      state.incomingContact = formatIncoming(state, contact);
    },
    setMeetingParticipants (state, { participants, currentUser }) {
      participants = participants || [];
      for (let i = 0; i < participants.length; i++) {
        if (participants[i].userhash) {
          const user = state.userPool.find((elem) => elem.userhash === participants[i].userhash);
          if (user) {
            participants[i] = {
              ...participants[i],
              ...user
            };
          }
        }
      }
      state.meetingParticipants = participants;
    },
    setOutgoingContact (state, contact) {
      state.outgoingContact = contact;
    },
    setPresence (state, { presence, isgroup }) {
      if (isgroup) {
        state.presence = presence;
      } else {
        presence.forEach(element => {
          if (!state.presence.find(elem => elem === element)) {
            state.presence.push(element);
          }
        });
      }
    },
    setRecentCalls (state, recentCalls) {
      state.recentCalls = recentCalls;
    },
    setSearchResults (state, results) {
      state.searchResults = results;
    },
    updateContact (state, contact) {
      let index = -1;
      if ('id' in contact) {
        index = state.contacts.findIndex(element => {
          return contact.id === element.id;
        });
      } else if ('userhash' in contact) {
        index = state.contacts.findIndex(element => {
          return contact.userhash === element.userhash;
        });
      }
      if (index > -1) {
        Object.keys(contact).forEach(key => {
          if (key !== 'id') {
            state.contacts[index][key] = contact[key];
          }
        });
      }
      if (contact.userhash) {
        index = state.userPool.findIndex(element => {
          return contact.userhash === element.userhash;
        });
        if (index > -1) {
          const user = state.userPool[index];
          if ('userstatus' in contact) {
            user.userstatus = contact.userstatus;
            user.presence = contact.userstatus === 1 ? true : user.presence;
          }
          Vue.set(state.userPool, index, user);
        }
      }
    },
    updateIncomingContact (state, contact) {
      if (state.incomingContact) {
        Object.keys(contact).forEach(key => {
          state.incomingContact[key] = contact[key];
        });
      }
    },
    updateOutgoingContact (state, contact) {
      if (state.outgoingContact) {
        Object.keys(contact).forEach(key => {
          state.outgoingContact[key] = contact[key];
        });
      }
    }
  },
  actions: {
    async addContacts ({ commit, dispatch }, contacts) {
      await dispatch('fetchIfNotExist', contacts);
      await commit('addContacts', contacts);
      commit('markContacts');
    },
    async initContacts ({ commit }, { contacts, users }) {
      try {
        await commit('addUserToPool', users);
        await commit('setContacts', contacts);
        commit('markContacts');
      } catch (error) {
        // console.error('initContacts error :', error);
      }
    },
    async fetchIfNotExist ({ commit, getters }, users) {
      const notListed = [];
      let fetchedUsers = [];
      for (let i = 0; i < users.length; i++) {
        if (
          users[i].userhash &&
          !getters.userPool.find(user => user.userhash === users[i].userhash)
        ) {
          notListed.push(users[i].userhash);
        }
      }
      if (notListed.length > 0) {
        fetchedUsers = await fetchUsers(notListed);
      }
      if (fetchedUsers.length > 0) {
        await commit('addUserToPool', fetchedUsers);
      }
    },
    async removeContact ({ commit }, contact) {
      await commit('removeContact', contact);
      commit('markContacts');
    },
    resetStore ({ commit }) {
      commit('setContacts', []);
      commit('setPresence', { presence: [], isgroup: true });
    },
    setIncomingContact ({ commit }, contact) {
      commit('setIncomingContact', contact);
    },
    setOutgoingContact ({ commit }, contact) {
      commit('setOutgoingContact', contact);
    },
    async setSearchResults ({ commit, dispatch, rootGetters }, results) {
      await dispatch(
        'fetchIfNotExist',
        (results || []).filter(item => item.userhash)
      );
      commit('setSearchResults', results);
      if (!rootGetters['AuthStore/profile']) {
        await commit('setPresence', {
          presence: (results || [])
            .filter(elem => !!elem.userhash)
            .map(elem => elem.userhash),
          isgroup: true
        });
        commit('markPresence');
      }
    },
    setMeetingParticipants ({ commit, rootGetters }, participants) {
      commit('setMeetingParticipants', {
        participants, currentUser: rootGetters['AuthStore/profile']
      });
    },
    async setRecentCalls ({ commit, dispatch }, recentCalls) {
      await dispatch(
        'fetchIfNotExist',
        (recentCalls || [])
          .filter(item => item.registeredid)
          .map(recent => ({ userhash: recent.registeredid }))
      );
      commit('setRecentCalls', recentCalls);
    },
    async updateContact ({ commit }, contact) {
      try {
        await commit('updateContact', contact);
      } catch (error) {}
    },
    async updateIncomingContact ({ commit }, contact) {
      commit('updateIncomingContact', contact);
    },
    async updateOutgoingContact ({ commit }, contact) {
      commit('updateOutgoingContact', contact);
    },
    async updatePresence ({ commit }, { presence, isgroup }) {
      try {
        await commit('setPresence', { presence, isgroup });
        await commit('markPresence');
      } catch (error) {}
    }
  }
};
